import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User } from 'lucide-react';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';

// Minimal client-side email sanity + disposable domain block. Backend validates authoritatively.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const CLIENT_DISPOSABLE = new Set([
  'mailinator.com','10minutemail.com','guerrillamail.com','sharklasers.com','grr.la',
  'yopmail.com','tempmail.com','temp-mail.org','temp-mail.io','throwawaymail.com',
  'fakeinbox.com','trashmail.com','dispostable.com','getnada.com','maildrop.cc',
  'mohmal.com','tempr.email','spam4.me','burnermail.io','emailondeck.com',
]);
const checkEmail = (email) => {
  const e = (email || '').trim().toLowerCase();
  if (!EMAIL_REGEX.test(e)) return 'Please enter a valid email address';
  const domain = e.split('@')[1] || '';
  if (CLIENT_DISPOSABLE.has(domain)) return 'Disposable / temporary emails are not allowed';
  for (const bad of CLIENT_DISPOSABLE) if (domain.endsWith('.' + bad)) return 'Disposable / temporary emails are not allowed';
  return '';
};

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [emailWarn, setEmailWarn] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleEmailChange = (v) => {
    setFormData({ ...formData, email: v });
    // Only show warning during signup and after user has typed @
    if (!isLogin && v.includes('@')) setEmailWarn(checkEmail(v));
    else setEmailWarn('');
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogle = () => {
    const redirectUrl = window.location.origin + '/auth';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // Client-side email validation on signup
    if (!isLogin) {
      const msg = checkEmail(formData.email);
      if (msg) { setError(msg); return; }
    }
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
      } else {
        await register(formData.name, formData.email, formData.password);
      }
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <div className="min-h-screen flex items-center justify-center px-6 pt-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="glass-heavy rounded-2xl p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-black font-heading text-white mb-2">
                {isLogin ? 'Welcome Back' : 'Get Started'}
              </h1>
              <p className="text-zinc-400">
                {isLogin ? 'Login to your account' : 'Create your account'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 bg-[#050505] border border-white/10 rounded-lg text-white focus:border-brand-primary focus:ring-1 focus:ring-brand-primary/50 outline-none transition-all"
                      placeholder="John Doe"
                      required
                      data-testid="name-input"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 bg-[#050505] border rounded-lg text-white focus:ring-1 outline-none transition-all ${emailWarn ? 'border-red-500/60 focus:border-red-500 focus:ring-red-500/40' : 'border-white/10 focus:border-brand-primary focus:ring-brand-primary/50'}`}
                    placeholder="you@example.com"
                    required
                    data-testid="email-input"
                  />
                </div>
                {emailWarn && (
                  <div className="mt-1.5 text-xs text-red-400" data-testid="email-warning">{emailWarn}</div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-[#050505] border border-white/10 rounded-lg text-white focus:border-brand-primary focus:ring-1 focus:ring-brand-primary/50 outline-none transition-all"
                    placeholder="••••••••"
                    required
                    data-testid="password-input"
                  />
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm" data-testid="auth-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || (!isLogin && !!emailWarn)}
                className="w-full py-3 bg-brand-primary text-black font-semibold rounded-lg hover:glow-primary transition-all disabled:opacity-50"
                data-testid="auth-submit-btn"
              >
                {loading ? 'Please wait...' : (isLogin ? 'Login' : 'Create Account')}
              </button>
            </form>

            {/* Divider */}
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-xs text-zinc-500 uppercase tracking-wider">or</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            {/* Google Sign-in */}
            <button
              type="button"
              onClick={handleGoogle}
              disabled={loading}
              className="w-full py-3 flex items-center justify-center gap-3 bg-white text-[#1F1F1F] font-semibold rounded-lg hover:bg-zinc-100 transition-all disabled:opacity-50"
              data-testid="google-signin-btn"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Continue with Google
            </button>

            {!isLogin && <p className="text-[10px] text-zinc-600 text-center mt-3">By continuing, you agree to our <a href="/terms" className="text-zinc-500 underline">Terms</a> & evaluation model.</p>}

            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError('');
                }}
                className="text-zinc-400 hover:text-brand-primary transition-colors"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Login'}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Auth;